import { useCallback, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import EndpointsEnum from "../models/EndpointsEnum";
import IInventoryResetProgressDTO from "../models/DTO/IInventoryResetProgressDTO";
import { IBackgroundTask } from "../contexts/BackgroundTasksContext";

/**
 * Hook to manage background tasks with socket listeners
 * Provides real-time progress updates for long-running operations
 */
export const useBackgroundTasks = (socket: Socket | null) => {
    const [tasks, setTasks] = useState<IBackgroundTask[]>([]);

    const activeTaskCount = tasks.filter((t) => t.status === "started" || t.status === "processing").length;

    const upsertTask = useCallback((task: IBackgroundTask) => {
        setTasks((prev) => {
            const existingIndex = prev.findIndex((t) => t.id === task.id);
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = task;
                return updated;
            }
            return [...prev, task];
        });
    }, []);

    const removeTask = useCallback((taskId: string) => {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
    }, []);

    const clearCompletedTasks = useCallback(() => {
        setTasks((prev) => prev.filter((t) => t.status === "started" || t.status === "processing"));
    }, []);

    const updateFromInventoryResetProgress = useCallback(
        (progress: IInventoryResetProgressDTO) => {
            const taskId = `inventory_reset_${progress.catalogGuid}`;
            const progressPercent = progress.totalItems > 0 ? (progress.processedItems / progress.totalItems) * 100 : 0;

            const task: IBackgroundTask = {
                id: taskId,
                type: "inventory_reset",
                name: "Inventory Reset",
                status: progress.status,
                progress: progressPercent,
                details: {
                    processedItems: progress.processedItems,
                    totalItems: progress.totalItems,
                    currentBatch: progress.currentBatch,
                    totalBatches: progress.totalBatches,
                    currentItem: progress.currentSku,
                },
                errorMessage: progress.errorMessage,
                startedAt: new Date(),
            };

            if (progress.status === "completed" || progress.status === "error") {
                task.completedAt = new Date();
            }

            upsertTask(task);

            // Auto-remove completed tasks after 5 seconds
            if (progress.status === "completed") {
                setTimeout(() => {
                    removeTask(taskId);
                }, 5000);
            }
        },
        [upsertTask, removeTask]
    );

    // Set up socket listener for inventory reset progress
    useEffect(() => {
        if (!socket) return;

        const handleInventoryResetProgress = (progress: IInventoryResetProgressDTO) => {
            updateFromInventoryResetProgress(progress);
        };

        socket.on(EndpointsEnum.InventoryResetProgress, handleInventoryResetProgress);

        return () => {
            socket.off(EndpointsEnum.InventoryResetProgress, handleInventoryResetProgress);
        };
    }, [socket, updateFromInventoryResetProgress]);

    return {
        tasks,
        activeTaskCount,
        upsertTask,
        removeTask,
        clearCompletedTasks,
        updateFromInventoryResetProgress,
    };
};

export default useBackgroundTasks;
